import type Claims from "niteroi/packages/air-passenger-rights/controllers/Claims.controller";
import ResourceModel from "sap/ui/model/resource/ResourceModel";
import {ClaimStatuses, IClaim} from "niteroi/packages/air-passenger-rights/models/interfaces";

class Formatter {
    public async getFlightIncidentTypeFromCode(this: Claims, code: number): Promise<string> {
        let textKey;
        switch (code) {
            case 1:
                textKey = "airPassengerRightsClaimsOptionIncidentTypeDelay";
                break;
            case 2:
                textKey = "airPassengerRightsClaimsOptionIncidentTypeCancellation";
                break;
            case 3:
                textKey = "airPassengerRightsClaimsOptionIncidentTypeDeniedBoarding";
                break;
            case 4:
                textKey = "airPassengerRightsClaimsOptionIncidentTypeDowngrading";
                break;
            default:
                textKey = "airPassengerRightsClaimsOptionIncidentTypeNone";
        }

        const component = this.getOwnerComponent();
        const localizationModel = component?.getModel("i18n") as ResourceModel | undefined;
        const resourceBundle = await localizationModel?.getResourceBundle();
        return resourceBundle?.getText(textKey) ?? textKey ?? "";
    }

    public async getClaimStatusFromCode(this: Claims, claimStatus: ClaimStatuses | string | number): Promise<string> {
        const normalizedStatus = typeof claimStatus === "string"
            ? Number.parseInt(claimStatus, 10)
            : claimStatus;

        const textKeyByStatus: Record<number, string> = {
            [ClaimStatuses.DRAFT]: "airPassengerRightsClaimsAddEditStatusDraft",
            [ClaimStatuses.READY]: "airPassengerRightsClaimsOptionClaimStatusReady",
            [ClaimStatuses.SUBMITTED]: "airPassengerRightsClaimsOptionClaimStatusSubmitted",
            [ClaimStatuses.NEGATIVE_RESPONSE]: "airPassengerRightsClaimsOptionClaimStatusNegativeResponse",
            [ClaimStatuses.READY_FOR_APPEAL]: "airPassengerRightsClaimsOptionClaimStatusReadyForAppeal",
            [ClaimStatuses.APPEAL_SUBMITTED]: "airPassengerRightsClaimsOptionClaimStatusAppealSubmitted",
            [ClaimStatuses.NEGATIVE_AFTER_APPEAL]: "airPassengerRightsClaimsOptionClaimStatusNegativeAfterAppeal",
            [ClaimStatuses.WAITING_PAYMENT]: "airPassengerRightsClaimsOptionClaimStatusWaitingPayment",
            [ClaimStatuses.PAYMENT_CONFIRMED]: "airPassengerRightsClaimsOptionClaimStatusPaymentConfirmed",
            [ClaimStatuses.PAID]: "airPassengerRightsClaimsOptionClaimStatusPaid",
            [ClaimStatuses.PENDING_CONTRACT]: "airPassengerRightsClaimsOptionClaimStatusPendingContract",
            [ClaimStatuses.PENDING_DOCUMENTS]: "airPassengerRightsClaimsOptionClaimStatusPendingDocuments"
        };

        const component = this.getOwnerComponent();
        const localizationModel = component?.getModel("i18n") as ResourceModel | undefined;
        const resourceBundle = await localizationModel?.getResourceBundle();
        return resourceBundle?.getText(textKeyByStatus[normalizedStatus] ?? "airPassengerRightsClaimsOptionClaimStatusUnknown") ?? textKeyByStatus[normalizedStatus] ?? "";
    }

    public getClaimIconFromStatus(claimStatus: ClaimStatuses): string {
        let icon;
        switch (claimStatus) {
            case ClaimStatuses.DRAFT:
                icon = "sap-icon://write-new-document";
                break;
            case ClaimStatuses.READY:
                icon = "sap-icon://checklist-item";
                break;
            case ClaimStatuses.SUBMITTED:
                icon = "sap-icon://BusinessSuiteInAppSymbols/icon-sms-send-delayed";
                break;
            case ClaimStatuses.NEGATIVE_RESPONSE:
                icon = "sap-icon://decline";
                break;
            case ClaimStatuses.READY_FOR_APPEAL:
                icon = "sap-icon://repost";
                break;
            case ClaimStatuses.APPEAL_SUBMITTED:
                icon = "sap-icon://workflow-tasks";
                break;
            case ClaimStatuses.NEGATIVE_AFTER_APPEAL:
                icon = "sap-icon://thumb-down";
                break;
            case ClaimStatuses.WAITING_PAYMENT:
            case ClaimStatuses.PAYMENT_CONFIRMED:
                icon = "sap-icon://pending";
                break;
            case ClaimStatuses.PAID:
                icon = "sap-icon://BusinessSuiteInAppSymbols/icon-money-withdrawal";
                break;
            case ClaimStatuses.PENDING_CONTRACT:
            case ClaimStatuses.PENDING_DOCUMENTS:
                icon = "sap-icon://request";
                break;
            default:
                icon = "sap-icon://accept";
        }
        return icon;
    }

    public translateKey(this: Claims, text?: string): string|null {
        return text ? this.getText(text) : text;
    }

    public async getRowCounter(this: Claims, rows: Array<IClaim>, tableRowCount: string, tableNoResults: string): Promise<string> {
        const component = this.getOwnerComponent();
        const localizationModel = component?.getModel("i18n") as ResourceModel | undefined;
        const resourceBundle = await localizationModel?.getResourceBundle();

        if (rows && rows.length > 0) {
            return resourceBundle?.getText(tableRowCount, [rows.length]) ?? tableRowCount ?? "";
        }
        return resourceBundle?.getText(tableNoResults) ?? tableNoResults ?? "";
    }
}

const formatter = new Formatter();
export default formatter;
